/* eslint-disable no-undef, consistent-return */

/**
 *
 *  /EmailPreferences
 *
 */

import React, { useEffect, useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Graphql

// Components
import CopyEmailTemplate from './CopyEmailTemplate'
import CategorySetter from './Setters/Category'
import TemplateSetter from './Setters/Template'
import SmsSetter from './Setters/Sms'

// Utils

import {
	ActionContainer,
	ActionSettings,
	ActionBox,
	Container,
	ParentContainer,
	EventContainer,
	HeaderBox,
	CallBox,
	ConditionalBox,
	InformationDialog,
	TemplateSelectorContainer,
	HLineShort,
	HLineLong,
	HLine2,
} from './styles'
import { template_indexes } from './email-timelines'

function TemplateSelector({
	templates_list,
	template_index,
	communication_preference,
	action,
	copyEmailTemplate,
	deleteEmailTemplate,
	origin,
	for_user,
	locked_by_organization,
	locked_by_user_role,
}) {
	const templates = templates_list.filter((template) => template.template_index === template_index)

	const handleDeleteTemplate = (template_id) => {
		swal({
			title: 'Are you sure you want to Delete this Email Template?',
			type: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, Delete this Email Template!',
			cancelButtonText: 'Cancel',
			confirmButtonClass: 'btn btn-danger d-inline',
			cancelButtonClass: 'btn btn-default d-inline',
			buttonsStyling: false,
		}).then(
			() => deleteEmailTemplate(template_id),
			() => {},
		)
	}

	const timeline_locked = locked_by_organization || locked_by_user_role

	return (
		<React.Fragment>
			{locked_by_organization ? (
				<div className="header px-3 py-2">
					Email Template for <b>{action.title}</b> selected by Organization:
				</div>
			) : (
				<div className="header px-3 py-2">
					Select Email Template for <b>{action.title}</b>:
				</div>
			)}

			{templates.map((template, index) => (
				<React.Fragment key={index}>
					<hr />

					<div className="template d-flex justify-content-between mx-1 pl-3 pr-2 py-2">
						{timeline_locked ? (
							<div className="d-flex">
								<span style={{ lineHeight: '23px', width: '25px' }}>
									{Boolean(template.default) && (
										<FontAwesomeIcon icon="check-circle" className="text-primary" />
									)}
								</span>
								<span
									className={template.global_default ? 'text-muted' : ''}
									style={template.global_default ? { fontStyle: 'oblique' } : {}}
								>
									{template.name}
								</span>
							</div>
						) : (
							<TemplateSetter
								communication_preference={communication_preference}
								template={template}
								templates={templates}
								disabled={timeline_locked}
							/>
						)}

						<div className="buttons">
							{!template.global_default && template.can_edit && !timeline_locked && (
								<React.Fragment>
									<Link
										style={{ textWrap: 'nowrap' }}
										className="btn btn-warning btn-sm ml-1"
										to={`/email_templates/${template.id}/edit#${origin}`}
									>
										Edit
									</Link>

									<button
										className="btn btn-danger btn-sm ml-1"
										onClick={() => handleDeleteTemplate(template.id)}
									>
										Delete
									</button>
								</React.Fragment>
							)}

							{!timeline_locked && (
								<CopyEmailTemplate
									email_template={template}
									copyEmailTemplate={copyEmailTemplate}
									select_user={for_user}
								/>
							)}

							<a
								className="btn btn-success btn-sm ml-1"
								target="_blank"
								href={`${process.env.API_URL}/email-templates/view/${template.id}`}
							>
								Preview
							</a>

							<a
								className="btn btn-purple btn-sm ml-1"
								target="_blank"
								href={`${process.env.API_URL}/email-templates/${template.id}`}
							>
								Download
							</a>
						</div>
					</div>
				</React.Fragment>
			))}
		</React.Fragment>
	)
}

function EmailTemplateAction({
	action,
	toggleShowDescription,
	toggleShowTemplateSelector,
	communication_preference,
	...props
}) {
	const has_active_emails = communication_preference.contact_emails[action.email_name]
	const has_active_sms = communication_preference.contact_sms[action.sms_name]

	const timeline_locked = props.locked_by_organization || props.locked_by_user_role

	const templates = props.email_templates.filter((template) => template.template_index === action.template_index)

	return (
		<ActionBox className={`${props.is_first_event ? '' : 'bottom-line'} ${props.is_last_event ? '' : 'top-line'}`}>
			<p className="header" style={{ opacity: has_active_sms || has_active_emails ? '1' : '0.6' }}>
				{action.title}
			</p>

			{action.action !== 'call' && <hr className="type-2" />}

			{!!action.email_name && (
				<div className="selector" style={{ paddingRight: props.hide_switches ? '10px' : 0 }}>
					<span>Email Service</span>

					<div className="d-flex align-items-center">
						{props.template}

						{templates?.length > 0 && (
							<FontAwesomeIcon
								icon="cog"
								className="settings"
								onClick={() => toggleShowTemplateSelector(action.id)}
							/>
						)}

						{action.email_description && (
							<FontAwesomeIcon
								icon="info-circle"
								className="information"
								onClick={() =>
									toggleShowDescription({
										id: action.id,
										title: action.title,
										description: action.email_description,
										show_email: true,
									})
								}
							/>
						)}

						{props.locked_by_organization && (
							<FontAwesomeIcon
								className="locked"
								icon="lock"
								data-tip="The Timeline is standardized at the organization level."
								data-for="context-tooltip"
							/>
						)}

						{!props.hide_switches && (
							<CategorySetter
								communication_preference={communication_preference}
								category_name={action.email_name}
								category_title={action.title}
								disabled={timeline_locked}
							/>
						)}
					</div>
				</div>
			)}

			{!!action.email_name && !!action.sms_name && <hr />}

			{!!action.sms_name && (
				<div className="selector" style={{ paddingRight: props.hide_switches ? '10px' : 0 }}>
					<span>SMS Service</span>

					<div className="d-flex align-items-center">
						{action.sms_description && (
							<FontAwesomeIcon
								icon="info-circle"
								className="information"
								onClick={() =>
									toggleShowDescription({
										id: action.id,
										title: action.title,
										description: action.sms_description,
										show_sms: true,
									})
								}
							/>
						)}

						{props.locked_by_organization && (
							<FontAwesomeIcon
								className="locked"
								icon="lock"
								data-tip="The Timeline is standardized at the organization level."
								data-for="context-tooltip"
							/>
						)}

						{!props.hide_switches && (
							<SmsSetter
								communication_preference={communication_preference}
								sms_type={action.sms_name}
								sms_title={action.title}
								disabled={timeline_locked}
							/>
						)}
					</div>
				</div>
			)}
		</ActionBox>
	)
}

function ActionLegends({ event, show_description, show_template_selector, ...props }) {
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				position: 'relative',
				alignSelf: 'stretch',
				flexGrow: 1,
			}}
		>
			{show_template_selector === event.id && (
				<React.Fragment>
					<div>
						<HLine2 />
					</div>

					<ActionSettings>
						<div className="card-container">
							<TemplateSelectorContainer id={`${event.id}-template-selector-box`}>
								<TemplateSelector
									action={event}
									templates_list={props.email_templates}
									template_index={event.template_index}
									{...props}
								/>
							</TemplateSelectorContainer>
						</div>
					</ActionSettings>
				</React.Fragment>
			)}

			{show_description?.id === event.id && (
				<React.Fragment>
					<div>
						<HLine2 />
					</div>

					<ActionSettings>
						<div className="card-container">
							<TemplateSelectorContainer>
								<div className="header hint px-3 py-2">
									<FontAwesomeIcon icon="info-circle" className="text-primary mr-1" />
									Tool Tips:
								</div>
								<div className="text-left px-4 mb-2">{show_description.description}</div>
							</TemplateSelectorContainer>
						</div>
					</ActionSettings>
				</React.Fragment>
			)}
		</div>
	)
}

function Timeline({ timeline, ...props }) {
	return (
		<React.Fragment>
			{timeline.map((section, section_index) => {
				const is_timeline_item = timeline.length - 1 === section_index

				if (section.type === 'header') {
					return (
						<Container className="d-flex">
							<div className="my-3">
								{section.is_conditional ? (
									<ConditionalBox className="mb-3">Conditional</ConditionalBox>
								) : (
									<React.Fragment>
										{section.events.map((event) => (
											<HeaderBox>{event.title}</HeaderBox>
										))}
									</React.Fragment>
								)}
							</div>
						</Container>
					)
				}

				if (section.type === 'call') {
					const event = section.events[0]

					return (
						<Container className="d-flex">
							<div className="descriptions" />

							<EventContainer className={`top-line ${is_timeline_item ? '' : 'bottom-line'}`}>
								<HLineLong />

								<ActionContainer>
									<HLineShort />

									<CallBox className="d-flex align-items-center justify-content-center">
										<p className="header">{event.title}</p>

										<FontAwesomeIcon
											icon="info-circle"
											className="clickable ml-2"
											onClick={() =>
												props.toggleShowDescription({
													id: event.id,
													description: event.description,
													show_email: true,
												})
											}
										/>
									</CallBox>
								</ActionContainer>

								<ActionLegends event={event} {...props} />
							</EventContainer>
						</Container>
					)
				}

				return (
					<Container className="d-flex">
						<div className="descriptions">
							{section?.descriptions?.map((description) => (
								<span>
									<b>{description}</b>
								</span>
							))}
						</div>

						<EventContainer className={`top-line ${is_timeline_item ? '' : 'bottom-line'}`}>
							<div className="multi-description-lines">
								{!section.descriptions ? (
									<div>
										<HLineLong />
									</div>
								) : (
									<React.Fragment>
										{section.descriptions?.map((_, index) => {
											let lines = ''

											if (section.descriptions.length === 1 || section.events.length > 1) {
												lines = ''
											} else if (index === 0) {
												lines = 'bottom-line'
											} else if (section.descriptions.length - 1 === index) {
												lines = 'top-line'
											} else {
												lines = 'top-line bottom-line'
											}

											return (
												<div className={lines}>
													<HLineLong />
												</div>
											)
										})}
									</React.Fragment>
								)}
							</div>

							<div className="event">
								{section.events.map((event, index) => {
									let lines = ''
									const is_first_event = index === 0
									const is_last_event = section.events.length - 1 === index

									if (section.events.length === 1) {
										lines = ''
									} else if (is_first_event) {
										lines = 'bottom-line'
									} else if (is_last_event) {
										lines = 'top-line'
									} else {
										lines = 'top-line bottom-line'
									}

									return (
										<ActionContainer className={lines}>
											<HLineShort />

											<EmailTemplateAction
												action={event}
												is_first_event={is_first_event}
												is_last_event={is_last_event}
												{...props}
											/>

											<ActionLegends event={event} {...props} />
										</ActionContainer>
									)
								})}
							</div>
						</EventContainer>
					</Container>
				)
			})}
		</React.Fragment>
	)
}

function Information({ show_description, show_template_selector, locked_by_organization, category, organization }) {
	return (
		<div className="d-flex">
			<div style={{ width: '50%' }} />
			<div style={{ position: 'relative', width: '50%' }}>
				{!(show_description || show_template_selector) && (
					<InformationDialog className="card">
						<div className="card-body ccontainer">
							<h4 className="mb-3">How to Use Timeline</h4>

							<div className="d-flex mt-3">
								<FontAwesomeIcon
									className="text-warning"
									icon="cog"
									size="lg"
									style={{ marginRight: '10px' }}
								/>
								<p>Click the gear icon to select and edit email templates.</p>
							</div>

							<div className="d-flex mt-3">
								<FontAwesomeIcon
									className="text-primary"
									icon="info-circle"
									size="lg"
									style={{ marginRight: '10px' }}
								/>
								<p>Click the information icon to read additional details and suggested tips.</p>
							</div>

							{locked_by_organization && (
								<div className="d-flex mt-3">
									<FontAwesomeIcon
										className="text-danger"
										icon="lock"
										size="lg"
										style={{ marginLeft: '2px', marginRight: '10px' }}
									/>
									<p>The Timeline is standardized at the organization level. ({organization.name})</p>
								</div>
							)}

							<div className="d-flex mt-3">
								<span className="call box">Outbound Call</span>
								{category === 'event_567' ? (
									<p>Acquireup calls each registrant to confirm.</p>
								) : (
									<React.Fragment>
										{category === 'event' ? (
											<p>
												Calls are typically made by the Event host or someone within the host’s
												office. If you prefer Acquireup calls on your behalf, please inquire
												with your Marketing Consultant.
											</p>
										) : (
											<p>
												Calls are typically made by the event host or someone within the host's
												office.
											</p>
										)}
									</React.Fragment>
								)}
							</div>

							<div className="d-flex mt-3">
								<span className="conditional box">Conditional</span>
								<p>
									Conditional Emails and SMS are deployed in specific scenarios and occur alongside
									the general timeline.
								</p>
							</div>
						</div>
					</InformationDialog>
				)}
			</div>
		</div>
	)
}

function EmailPreferences({ email_timeline, buildTooltip, category, default_template_index, ...props }) {
	const [show_description, setShowDescription] = useState(null)
	const [show_template_selector, setTemplateSelector] = useState('')

	useEffect(() => {
		buildTooltip && buildTooltip()
	}, [buildTooltip])

	useEffect(() => {
		if (default_template_index !== null && default_template_index !== undefined) {
			const index = template_indexes[default_template_index]

			if (!!index) {
				setTemplateSelector(index)

				const timeoutId = setTimeout(() => {
					const el = document.getElementById(`${index}-template-selector-box`)
					el?.scrollIntoView({ behavior: 'smooth' })
				}, 500)

				return () => clearTimeout(timeoutId)
			}
		}
	}, [default_template_index])

	const toggleShowTemplateSelector = (new_index) => {
		setShowDescription(null)
		setTemplateSelector(show_template_selector === new_index ? '' : new_index)
	}

	const toggleShowDescription = (new_event) => {
		setTemplateSelector('')

		if (show_description?.show_email) {
			setShowDescription(show_description?.id === new_event.id && new_event.show_email ? null : new_event)
		} else if (show_description?.show_sms) {
			setShowDescription(show_description?.id === new_event.id && new_event.show_sms ? null : new_event)
		} else {
			setShowDescription(new_event)
		}
	}

	const timeline_props = {
		show_description,
		show_template_selector,
		toggleShowTemplateSelector,
		toggleShowDescription,
		...props,
	}

	return (
		<React.Fragment>
			<Information
				category={category}
				organization={props.organization}
				show_description={show_description}
				show_template_selector={show_template_selector}
				locked_by_organization={props.locked_by_organization}
			/>

			<ParentContainer>
				<Container>
					<Timeline timeline={email_timeline.main} {...timeline_props} />
					<Timeline timeline={email_timeline.conditional} {...timeline_props} />
				</Container>
			</ParentContainer>
		</React.Fragment>
	)
}

TemplateSelector.propTypes = {
	templates_list: PropTypes.array,
	template_index: PropTypes.number,
	action: PropTypes.object,
	communication_preference: PropTypes.object,
	copyEmailTemplate: PropTypes.func,
	deleteEmailTemplate: PropTypes.func,
	locked_by_organization: PropTypes.bool,
	locked_by_user_role: PropTypes.bool,
	for_user: PropTypes.bool,
	origin: PropTypes.string,
}

EmailTemplateAction.propTypes = {
	user: PropTypes.object,
	action: PropTypes.object,
	communication_preference: PropTypes.object,
	toggleShowDescription: PropTypes.func,
	toggleShowTemplateSelector: PropTypes.func,
	is_first_event: PropTypes.bool,
	is_last_event: PropTypes.bool,
	hide_switches: PropTypes.bool,
	locked_by_organization: PropTypes.bool,
	locked_by_user_role: PropTypes.bool,
}

ActionLegends.propTypes = {
	email_templates: PropTypes.array,
	event: PropTypes.object,
	action: PropTypes.object,
	show_description: PropTypes.bool,
	show_template_selector: PropTypes.bool,
}

Information.propTypes = {
	show_description: PropTypes.bool,
	show_template_selector: PropTypes.bool,
	category: PropTypes.string,
	locked_by_organization: PropTypes.bool,
	organization: PropTypes.object,
}

Timeline.propTypes = {
	timeline: PropTypes.array,
	toggleShowDescription: PropTypes.func,
	toggleShowTemplateSelector: PropTypes.func,
}

EmailPreferences.propTypes = {
	email_timeline: PropTypes.array,
	category: PropTypes.string,
	locked_by_organization: PropTypes.bool,
	default_template_index: PropTypes.bool,
	organization: PropTypes.object,
	buildTooltip: PropTypes.func,
}

export default EmailPreferences
